import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Truck, Package, Shield, Clock, DollarSign, CheckCircle, Star, Boxes, Warehouse, ArrowRight, Wrench } from 'lucide-react';
import move1 from '../assets/moving/move1.jpg';
import move2 from '../assets/moving/move2.jpg';
import move3 from '../assets/moving/move3.jpg';
import move4 from '../assets/moving/move4.jpg';
import move5 from '../assets/moving/move5.jpg';
import move6 from '../assets/moving/move6.jpg';
import { apiClient } from '../utils/api.js';
import { sendContactEmail, isEmailNotificationsEnabled } from '../utils/email.js';
import SEO from '../components/SEO.js';
import Header from '../components/Header.js';
import Footer from '../components/Footer.js';
//import '../styles/pages/MovingService.css';

const MovingService = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'moving',
    message: '',
    address: '',
    preferredDate: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const emailNotificationsEnabled = useMemo(() => isEmailNotificationsEnabled, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await apiClient.post('/api/contact', formData);
      if (emailNotificationsEnabled) {
        try {
          await sendContactEmail({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            service: formData.service,
            message: formData.message,
            preferredDate: formData.preferredDate || 'Not provided',
            address: formData.address || 'Not provided'
          });
          setSubmitStatus('success');
        } catch (emailError) {
          console.warn('EmailJS send failed:', emailError);
          setSubmitStatus('partial');
        }
      } else {
        setSubmitStatus('success');
      }

      setFormData({
        name: '',
        email: '',
        phone: '',
        service: 'moving',
        message: '',
        address: '',
        preferredDate: ''
      });
    } catch (error) {
      setSubmitStatus('error');
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Sample Yelp reviews data
  const reviews = [
    {
      id: 1,
      name: "Sarah M.",
      rating: 5,
      date: "2 weeks ago",
      text: "Absolutely amazing service! The team was professional, efficient, and handled all our belongings with care. They even helped us pack some last-minute items. Highly recommend!",
      service: "Residential Move"
    },
    {
      id: 2,
      name: "Mike R.",
      rating: 5,
      date: "1 month ago",
      text: "Best moving company I've ever used. They were on time, careful with our furniture, and the price was very reasonable. Will definitely use them again for our next move.",
      service: "Long Distance Move"
    },
    {
      id: 3,
      name: "Jennifer L.",
      rating: 5,
      date: "3 weeks ago",
      text: "Professional, reliable, and affordable. They made our office move stress-free. The team worked quickly and efficiently. Five stars!",
      service: "Commercial Move"
    }
  ];

  const pricingHighlights = [
    '$109/hour - Cash/Zelle/Venmo payment',
    '$119/hour - Card payment',
    '3 hour minimum billing time',
    'No other hidden fees'
  ];

  return (
    <>
      <SEO
        title="Professional Moving Services - Local & Long Distance Moves | SAP Movers"
        description="Professional moving services including residential, commercial, and long-distance moves. Licensed, insured movers with packing services. Free estimates available."
        keywords="moving services, local movers, long distance moving, residential moving, commercial moving, packing services, professional movers"
        canonical="https://sapmovers.com/moving"
        ogTitle="Professional Moving Services - SAP Movers"
        ogDescription="Professional moving services with licensed, insured movers. Free estimates and competitive pricing."
      />

      <Header />

      {/* Hero Section */}
      <section className="hero">
        <h1>Professional Moving Services</h1>
        <p>Local and long-distance moves with full packing and unpacking services.</p>
        <Link to="/contact" className="cta-button">Get Free Moving Quote</Link>
      </section>

      {/* Services Offered */}
      <section className="services">
        <h2>Our Moving Services</h2>
        <div className="services-grid">
          <div className="service-card">
            <Truck size={48} color="var(--icon-blue)" style={{ marginBottom: '1rem' }} />
            <h3>Residential Moving</h3>
            <p>Complete home moving services from packing to unpacking. We handle everything so you can focus on your new beginning.</p>
          </div>
          
          <div className="service-card">
            <Truck size={48} color="var(--icon-blue)" style={{ marginBottom: '1rem' }} />
            <h3>Commercial Moving</h3>
            <p>Office and business relocations with minimal downtime. We work around your schedule to keep your business running.</p>
          </div>
          
          <div className="service-card">
            <Truck size={48} color="var(--icon-blue)" style={{ marginBottom: '1rem' }} />
            <h3>Long Distance Moving</h3>
            <p>Cross-country and interstate moves with secure transportation and tracking. Your belongings are safe with us.</p>
          </div>
          
          <div className="service-card">
            <Package size={48} color="var(--icon-blue)" style={{ marginBottom: '1rem' }} />
            <h3>Packing & Unpacking</h3>
            <p>Professional packing services using high-quality materials. We pack everything carefully to prevent damage.</p>
          </div>
          
          <div className="service-card">
            <Wrench size={48} color="var(--icon-blue)" style={{ marginBottom: '1rem' }} />
            <h3>Furniture Assembly</h3>
            <p>We disassemble and reassemble furniture safely. Your furniture will be ready to use in your new home.</p>
          </div>
          
          <div className="service-card">
            <Package size={48} color="var(--icon-blue)" style={{ marginBottom: '1rem' }} />
            <h3>Storage Solutions</h3>
            <p>Secure storage options for your belongings. Short-term or long-term storage with climate control available.</p>
          </div>

          <div className="service-card">
            <Boxes size={48} color="var(--icon-blue)" style={{ marginBottom: '1rem' }} />
            <h3>On-Site Moving Help</h3>
            <p>Need muscle without the truck? We help load, unload, or rearrange items anywhere on your property.</p>
          </div>

          <div className="service-card">
            <Warehouse size={48} color="var(--icon-blue)" style={{ marginBottom: '1rem' }} />
            <h3>Warehouse & Storage Moves</h3>
            <p>Specialized crews for warehouse, storage unit, and inventory relocations with careful item tracking.</p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <div className="pricing-container">
          <h2>Transparent Pricing</h2>
          

          <div className="pricing-highlights">
            <div className="pricing-highlight-card">
              <div className="pricing-highlight-header">
                
                <h3>Rates that cover everything you need</h3>
                <p>Professional movers, truck, equipment, padding, and basic coverage included with every booking.</p>
              </div>

              <ul className="pricing-highlight-list">
                {pricingHighlights.map((highlight) => (
                  <li key={highlight}>
                    <span className="pricing-icon">
                      <CheckCircle size={18} />
                    </span>
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>

            
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="services" style={{ background: '#f8fafc' }}>
        <h2>Why Choose Our Moving Services?</h2>
        <div className="services-grid">
        
          
          <div className="service-card">
            <CheckCircle size={48} color="var(--icon-blue)" style={{ marginBottom: '1rem' }} />
            <h3>Experienced Team</h3>
            <p>Professional movers with years of experience. We handle your belongings with care and efficiency.</p>
          </div>
          
          <div className="service-card">
            <Clock size={48} color="var(--icon-blue)" style={{ marginBottom: '1rem' }} />
            <h3>On-Time Service</h3>
            <p>Punctual and reliable service. We arrive on time and complete your move efficiently.</p>
          </div>
          
          <div className="service-card">
            <DollarSign size={48} color="var(--icon-blue)" style={{ marginBottom: '1rem' }} />
            <h3>Competitive Pricing</h3>
            <p>Transparent pricing with no hidden fees. Free estimates and competitive rates for all services.</p>
          </div>
        </div>
      </section>

      {/* Works Section */}
      <section className="works-section">
        <div className="works-container">
          <h2>Our Recent Moves</h2>
          <p>Snapshots from moves we’ve completed recently</p>
          <div className="works-gallery">
            {[move1, move2, move3, move4, move5, move6].map((photo, index) => (
              <div key={index} className="gallery-card">
                <img
                  src={photo}
                  alt={`Recent moving project ${index + 1}`}
                  className="gallery-image"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="reviews-section">
        <div className="reviews-container">
          <div className="reviews-header">
            <h2>What Our Customers Say</h2>
            
            
          </div>
          
          <div className="reviews-grid">
            {reviews.map((review) => (
              <div key={review.id} className="review-card">
                <div className="review-header">
                  <div className="reviewer-info">
                    <div className="reviewer-name">{review.name}</div>
                    <div className="review-date">{review.date}</div>
                  </div>
                  <div className="review-rating">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        size={16} 
                        color={i < review.rating ? "var(--icon-primary)" : "#e5e7eb"} 
                        fill={i < review.rating ? "var(--icon-primary)" : "none"}
                      />
                    ))}
                  </div>
                </div>
                <div className="review-service">{review.service}</div>
                <p className="review-text">"{review.text}"</p>
                <div className="review-source">
                  <span>Verified on Yelp</span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="reviews-cta">
            
            <a
              href="https://www.yelp.com/biz/sap-movers-irvine-7?osq=SAP+MOVERS"
              className="yelp-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Star size={16} />
              <span>View All Reviews</span>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="contact-form">
        <h2>Get Your Free Moving Quote</h2>
        <form onSubmit={handleSubmit}>
          <div className="stack-on-mobile">
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="stack-on-mobile">
            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="preferredDate">Preferred Move Date</label>
              <input
                type="date"
                id="preferredDate"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="address">Current Address</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Where are you moving from?"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="message">Additional Details *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="4"
              placeholder="Tell us about your move - size of home, special items, destination, etc."
              required
            />
          </div>
          
          {submitStatus === 'success' && (
            <div className="form-status success">
              ✓ Thank you! We'll contact you soon with your free quote.
            </div>
          )}

          {submitStatus === 'partial' && (
            <div className="form-status warning">
              ✓ Request saved, but email notification failed. We'll follow up manually.
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="form-status error">
              ✗ Sorry, there was an error. Please try again or call us directly.
            </div>
          )}
          
          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Get Free Moving Quote'}
          </button>
        </form>
      </section>

      <Footer />
    </>
  );
};

export default MovingService;
